import React, { useRef, useEffect, useCallback } from 'react';
import { SimulationResult } from '../../types';

interface Props {
  result: SimulationResult | null;
  running: boolean;
  width?: number;
  height?: number;
}

/**
 * Canvas that renders:
 *  - Animated particle dots arriving on the detector screen
 *  - Interference / probability heatmap overlaid
 */
export default function DetectorCanvas({
  result,
  running,
  width = 380,
  height = 220,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const hitsShownRef = useRef(0);

  const drawHeatmap = useCallback(
    (ctx: CanvasRenderingContext2D, dist: number[], w: number, h: number) => {
      const barW = w / dist.length;
      dist.forEach((val, i) => {
        const intensity = Math.floor(val * 255);
        // Blue-violet heatmap colour
        ctx.fillStyle = `rgba(99,102,241,${val * 0.5})`;
        ctx.fillRect(i * barW, 0, barW, h);
      });
    },
    []
  );

  const drawParticles = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      hits: { x: number; y: number; intensity: number }[],
      count: number
    ) => {
      hits.slice(0, count).forEach((hit) => {
        const alpha = 0.7 + hit.intensity * 0.3;
        ctx.beginPath();
        ctx.arc(hit.x, hit.y, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(99,102,241,${alpha})`;
        ctx.fill();
      });
    },
    []
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (!result) {
      ctx.clearRect(0, 0, width, height);
      // Draw empty screen with grid
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, width, height);
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 1;
      for (let x = 0; x < width; x += 20) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += 20) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      ctx.fillStyle = '#334155';
      ctx.font = '13px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('Detector Screen', width / 2, height / 2 - 8);
      ctx.fillStyle = '#475569';
      ctx.font = '11px system-ui';
      ctx.fillText('Press ▶ Run to start', width / 2, height / 2 + 10);
      return;
    }

    hitsShownRef.current = 0;
    cancelAnimationFrame(animFrameRef.current);

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, width, height);

      // Heatmap background
      drawHeatmap(ctx, result.probabilityDistribution, width, height);

      // Particles arriving incrementally
      hitsShownRef.current = Math.min(
        hitsShownRef.current + 4,
        result.detectorHits.length
      );
      drawParticles(ctx, result.detectorHits, hitsShownRef.current);

      // Label
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '10px system-ui';
      ctx.textAlign = 'left';
      ctx.fillText(
        `${hitsShownRef.current} / ${result.detectorHits.length} particles`,
        8,
        height - 6
      );

      if (hitsShownRef.current < result.detectorHits.length) {
        animFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [result, width, height, drawHeatmap, drawParticles]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ borderRadius: 8, display: 'block' }}
    />
  );
}
